import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, CheckCircle, Circle, List, LayoutGrid, Clock, ArrowUpDown, Edit2, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const columns = ['Action Required', 'Follow-Up', 'In Progress', 'Needs Review', 'In Execution', 'Completed'];
const taskTypes = ['General', 'Follow-up', 'Drawing', 'Order', 'Delivery', 'Payment'];
const priorities = ['High', 'Medium', 'Low'];

const colDotColors = {
  'Action Required': 'bg-[#3A8C8C]', 'Follow-Up': 'bg-[#C67D5B]', 'In Progress': 'bg-[#3A8C8C]',
  'Needs Review': 'bg-[#C67D5B]', 'In Execution': 'bg-[#7DA68A]', 'Completed': 'bg-[#7DA68A]'
};

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

function getPriorityDot(task) {
  if (!task.due_date || task.status === 'Completed') return null;
  const days = Math.ceil((new Date(task.due_date) - Date.now()) / 86400000);
  if (days < 0) return { color: 'bg-[#C4727F]', label: 'Overdue' };
  if (days <= 2) return { color: 'bg-[#C67D5B]', label: 'Urgent' };
  return { color: 'bg-[#7DA68A]', label: 'On Track' };
}

export default function ProjectTasksBoard({ projectId, projectName }) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('board'); // 'board' | 'list'
  const [showCreate, setShowCreate] = useState(false);
  const [showConfirm, setShowConfirm] = useState(null); // task id
  const [showDelete, setShowDelete] = useState(null); // task id
  const [editingTask, setEditingTask] = useState(null); // task object
  const [dragData, setDragData] = useState(null);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterAssigned, setFilterAssigned] = useState('all');
  const [sortCol, setSortCol] = useState('due_date');
  const [sortDir, setSortDir] = useState('asc');
  const [form, setForm] = useState({ title: '', assigned_to: '', assigned_name: '', priority: 'Medium', due_date: '', task_type: 'General', description: '' });

  const canCreate = user?.role === 'owner' || user?.role === 'team';

  const loadData = useCallback(async () => {
    try {
      const [t, tm] = await Promise.all([
        api.get(`/tasks?project_id=${projectId}`),
        api.get('/team')
      ]);
      setTasks(t.data);
      setTeam(tm.data);
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const createTask = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      await api.post('/tasks', { ...form, project_id: projectId, project_name: projectName, status: 'Action Required' });
      toast.success('Task created');
      setShowCreate(false);
      setForm({ title: '', assigned_to: '', assigned_name: '', priority: 'Medium', due_date: '', task_type: 'General', description: '' });
      loadData();
    } catch { toast.error('Failed to create'); }
  };

  const moveTask = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch { toast.error('Move failed'); loadData(); }
  };

  const completeTask = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: 'Completed' });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'Completed' } : t));
      setShowConfirm(null);
      toast.success('Task completed');
    } catch { toast.error('Failed'); }
  };

  const reopenTask = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: 'Action Required' });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'Action Required' } : t));
      toast.success('Task reopened');
    } catch { toast.error('Failed'); }
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setForm({
      title: task.title || '', assigned_to: task.assigned_to || '', assigned_name: task.assigned_name || '',
      priority: task.priority || 'Medium', due_date: task.due_date ? task.due_date.slice(0, 10) : '',
      task_type: task.task_type || 'General', description: task.description || ''
    });
    setShowCreate(true);
  };

  const saveEditTask = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      await api.put(`/tasks/${editingTask.id}`, { ...form });
      toast.success('Task updated');
      setShowCreate(false);
      setEditingTask(null);
      setForm({ title: '', assigned_to: '', assigned_name: '', priority: 'Medium', due_date: '', task_type: 'General', description: '' });
      loadData();
    } catch { toast.error('Failed to update'); }
  };

  const deleteTask = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setShowDelete(null);
      toast.success('Task deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleDragStart = (e, task) => { setDragData(task); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (e, col) => { e.preventDefault(); if (dragData && dragData.status !== col) moveTask(dragData.id, col); setDragData(null); };

  const filtered = tasks.filter(t => {
    if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
    if (filterType !== 'all' && t.task_type !== filterType) return false;
    if (filterAssigned !== 'all' && t.assigned_to !== filterAssigned) return false;
    return true;
  });

  const sortedList = [...filtered].sort((a, b) => {
    const av = a[sortCol] || '';
    const bv = b[sortCol] || '';
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="project-tasks-board">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h4 className="section-heading text-sm font-semibold font-['Manrope']">Tasks</h4>
          <span className="text-xs text-[#6B7280]">{tasks.length} total</span>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex rounded-lg border border-[#E8E6E1] overflow-hidden">
            <button onClick={() => setView('board')} data-testid="tasks-view-board"
              className={`p-1.5 transition-colors ${view === 'board' ? 'bg-[#3A8C8C] text-white' : 'text-[#6B7280] hover:bg-[#E8E6E1]'}`}>
              <LayoutGrid size={14} strokeWidth={1.5} />
            </button>
            <button onClick={() => setView('list')} data-testid="tasks-view-list"
              className={`p-1.5 transition-colors ${view === 'list' ? 'bg-[#3A8C8C] text-white' : 'text-[#6B7280] hover:bg-[#E8E6E1]'}`}>
              <List size={14} strokeWidth={1.5} />
            </button>
          </div>
          {canCreate && (
            <button onClick={() => setShowCreate(true)} data-testid="project-new-task-btn"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors">
              <Plus size={14} strokeWidth={1.5} /> New Task
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="h-8 w-28 rounded-lg border-[#E8E6E1] text-[11px]" data-testid="filter-priority">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            {priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="h-8 w-28 rounded-lg border-[#E8E6E1] text-[11px]" data-testid="filter-type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {taskTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAssigned} onValueChange={setFilterAssigned}>
          <SelectTrigger className="h-8 w-32 rounded-lg border-[#E8E6E1] text-[11px]" data-testid="filter-assigned">
            <SelectValue placeholder="Assigned To" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Members</SelectItem>
            {team.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Kanban Board View */}
      {view === 'board' && (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
          {columns.map(col => {
            const colTasks = filtered.filter(t => t.status === col);
            return (
              <div key={col} className="min-w-[200px] flex-1"
                onDragOver={handleDragOver} onDrop={e => handleDrop(e, col)}
                data-testid={`kanban-col-${col.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center gap-2 mb-2.5 px-1">
                  <div className={`w-2 h-2 rounded-full ${colDotColors[col]}`} />
                  <span className="text-[10px] font-semibold text-[#2C2C2E] uppercase tracking-wider">{col}</span>
                  <Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[9px] h-4 px-1 ml-auto">{colTasks.length}</Badge>
                </div>
                <div className="space-y-2 min-h-[80px]">
                  {colTasks.map(task => {
                    const dot = getPriorityDot(task);
                    const isCompleted = task.status === 'Completed';
                    return (
                      <div key={task.id} draggable onDragStart={e => handleDragStart(e, task)}
                        className={`glass-card rounded-xl p-3 cursor-grab active:cursor-grabbing hover:shadow-sm transition-all group ${isCompleted ? 'opacity-70' : ''}`}
                        data-testid={`task-card-${task.id}`}>
                        <div className="flex items-start gap-2">
                          {/* Completion checkbox */}
                          <button onClick={e => { e.stopPropagation(); isCompleted ? reopenTask(task.id) : setShowConfirm(task.id); }}
                            className="mt-0.5 shrink-0 transition-colors" data-testid={`task-check-${task.id}`}>
                            {isCompleted
                              ? <CheckCircle size={16} strokeWidth={1.5} className="text-[#7DA68A]" />
                              : <Circle size={16} strokeWidth={1.5} className="text-[#D4D0C8] hover:text-[#3A8C8C]" />
                            }
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-medium text-[#2C2C2E] leading-snug ${isCompleted ? 'line-through text-[#6B7280]' : ''}`}>
                              {task.title}
                            </p>
                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                              {task.assigned_name && (
                                <span className="text-[9px] text-[#6B7280] bg-[#E8E6E1] px-1.5 py-0.5 rounded">{task.assigned_name}</span>
                              )}
                              {task.due_date && (
                                <span className={`text-[9px] ${dot?.label === 'Overdue' ? 'text-[#C4727F] font-medium' : 'text-[#6B7280]'}`}>
                                  {formatDate(task.due_date)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            {dot && <div className={`w-2 h-2 rounded-full mt-1 ${dot.color}`} title={dot.label} />}
                            <div className="flex gap-0.5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={e => { e.stopPropagation(); openEditTask(task); }}
                                className="p-1 rounded hover:bg-[#E8E6E1] transition-colors" title="Edit" data-testid={`task-edit-${task.id}`}>
                                <Edit2 size={11} className="text-[#6B7280]" />
                              </button>
                              <button onClick={e => { e.stopPropagation(); setShowDelete(task.id); }}
                                className="p-1 rounded hover:bg-[#C4727F]/10 transition-colors" title="Delete" data-testid={`task-delete-${task.id}`}>
                                <Trash2 size={11} className="text-[#C4727F]" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="overflow-x-auto rounded-xl border border-[#E8E6E1]" data-testid="tasks-list-view">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FAFAF8]">
                <th className="w-8 px-2 py-2 border-b border-[#E8E6E1]" />
                {[
                  { key: 'title', label: 'Task' },
                  { key: 'assigned_name', label: 'Assigned To' },
                  { key: 'priority', label: 'Priority' },
                  { key: 'status', label: 'Status' },
                  { key: 'due_date', label: 'Due Date' },
                  { key: 'task_type', label: 'Type' },
                ].map(col => (
                  <th key={col.key} onClick={() => toggleSort(col.key)}
                    className="px-3 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-left border-b border-[#E8E6E1] cursor-pointer hover:text-[#2C2C2E]">
                    <span className="flex items-center gap-1">{col.label}
                      {sortCol === col.key && <ArrowUpDown size={10} className="text-[#3A8C8C]" />}
                    </span>
                  </th>
                ))}
                <th className="w-16 px-2 py-2 border-b border-[#E8E6E1]" />
              </tr>
            </thead>
            <tbody>
              {sortedList.map(task => {
                const dot = getPriorityDot(task);
                const isCompleted = task.status === 'Completed';
                return (
                  <tr key={task.id} className="hover:bg-[#FAFAF8]/50 transition-colors" data-testid={`task-list-row-${task.id}`}>
                    <td className="px-2 py-2 border-b border-[#E8E6E1]">
                      <button onClick={() => isCompleted ? reopenTask(task.id) : setShowConfirm(task.id)}
                        data-testid={`task-list-check-${task.id}`}>
                        {isCompleted
                          ? <CheckCircle size={15} strokeWidth={1.5} className="text-[#7DA68A]" />
                          : <Circle size={15} strokeWidth={1.5} className="text-[#D4D0C8] hover:text-[#3A8C8C]" />
                        }
                      </button>
                    </td>
                    <td className={`px-3 py-2 border-b border-[#E8E6E1] text-xs font-medium ${isCompleted ? 'line-through text-[#6B7280]' : 'text-[#2C2C2E]'}`}>
                      {task.title}
                    </td>
                    <td className="px-3 py-2 border-b border-[#E8E6E1] text-xs text-[#6B7280]">{task.assigned_name || '-'}</td>
                    <td className="px-3 py-2 border-b border-[#E8E6E1]">
                      <div className="flex items-center gap-1.5">
                        {dot && <div className={`w-2 h-2 rounded-full ${dot.color}`} />}
                        <span className="text-[10px] text-[#6B7280]">{task.priority}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 border-b border-[#E8E6E1]">
                      <Badge className={`text-[9px] ${isCompleted ? 'bg-[#7DA68A]/10 text-[#7DA68A]' : 'bg-[#E8E6E1] text-[#2C2C2E]'}`}>{task.status}</Badge>
                    </td>
                    <td className={`px-3 py-2 border-b border-[#E8E6E1] text-xs ${dot?.label === 'Overdue' ? 'text-[#C4727F] font-medium' : 'text-[#6B7280]'}`}>
                      {formatDate(task.due_date) || '-'}
                    </td>
                    <td className="px-3 py-2 border-b border-[#E8E6E1]">
                      <Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[9px]">{task.task_type || 'General'}</Badge>
                    </td>
                    <td className="px-2 py-2 border-b border-[#E8E6E1]">
                      <div className="flex gap-0.5 justify-end">
                        <button onClick={() => openEditTask(task)} className="p-1 rounded hover:bg-[#E8E6E1] transition-colors" title="Edit" data-testid={`task-list-edit-${task.id}`}>
                          <Edit2 size={12} className="text-[#6B7280]" />
                        </button>
                        <button onClick={() => setShowDelete(task.id)} className="p-1 rounded hover:bg-[#C4727F]/10 transition-colors" title="Delete" data-testid={`task-list-delete-${task.id}`}>
                          <Trash2 size={12} className="text-[#C4727F]" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {sortedList.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-xs text-[#6B7280]">No tasks found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Complete Confirmation Dialog */}
      <Dialog open={!!showConfirm} onOpenChange={() => setShowConfirm(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-['Manrope'] text-base">Mark as completed?</DialogTitle></DialogHeader>
          <p className="text-xs text-[#6B7280] mt-1">This task will be moved to the Completed column.</p>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setShowConfirm(null)} className="flex-1 h-9 rounded-lg border border-[#D4D0C8] text-xs font-medium text-[#6B7280] hover:bg-[#E8E6E1] transition-colors"
              data-testid="confirm-cancel">Cancel</button>
            <button onClick={() => showConfirm && completeTask(showConfirm)} data-testid="confirm-complete"
              className="flex-1 h-9 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors">Confirm</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-['Manrope'] text-base">Delete task?</DialogTitle></DialogHeader>
          <p className="text-xs text-[#6B7280] mt-1">This action cannot be undone. The task will be permanently removed.</p>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setShowDelete(null)} className="flex-1 h-9 rounded-lg border border-[#D4D0C8] text-xs font-medium text-[#6B7280] hover:bg-[#E8E6E1] transition-colors"
              data-testid="delete-cancel">Cancel</button>
            <button onClick={() => showDelete && deleteTask(showDelete)} data-testid="delete-confirm"
              className="flex-1 h-9 rounded-lg bg-[#C4727F] text-white text-xs font-medium hover:bg-[#B5616E] transition-colors">Delete</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create / Edit Task Dialog */}
      <Dialog open={showCreate} onOpenChange={v => { if (!v) { setShowCreate(false); setEditingTask(null); setForm({ title: '', assigned_to: '', assigned_name: '', priority: 'Medium', due_date: '', task_type: 'General', description: '' }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-['Manrope']">{editingTask ? 'Edit Task' : `New Task — ${projectName}`}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs text-[#6B7280] uppercase">Title</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                className="mt-1 h-10 rounded-xl border-[#D4D0C8]" data-testid="new-task-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B7280] uppercase">Assign To</Label>
                <Select value={form.assigned_to} onValueChange={v => { const m = team.find(x => x.id === v); setForm({ ...form, assigned_to: v, assigned_name: m?.name || '' }); }}>
                  <SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{team.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]"><SelectValue /></SelectTrigger>
                  <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B7280] uppercase">Due Date</Label>
                <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
                  className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Task Type</Label>
                <Select value={form.task_type} onValueChange={v => setForm({ ...form, task_type: v })}>
                  <SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]"><SelectValue /></SelectTrigger>
                  <SelectContent>{taskTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs text-[#6B7280] uppercase">Notes</Label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                className="mt-1 w-full h-16 px-4 py-2 bg-white border border-[#D4D0C8] rounded-xl text-sm resize-none" /></div>
            <button onClick={editingTask ? saveEditTask : createTask} data-testid="submit-new-task"
              className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors">{editingTask ? 'Update Task' : 'Create Task'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
